import { Capacitor } from '@capacitor/core';
import AccountBalanceIcon from '@mui/icons-material/AccountBalanceOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBackOutlined';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturnOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFileOutlined';
import BluetoothIcon from '@mui/icons-material/BluetoothOutlined';
import BrightnessAutoIcon from '@mui/icons-material/BrightnessAutoOutlined';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import CallIcon from '@mui/icons-material/CallOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRightOutlined';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import CloudIcon from '@mui/icons-material/CloudOutlined';
import ContactMailIcon from '@mui/icons-material/ContactMailOutlined';
import ContactPhoneIcon from '@mui/icons-material/ContactPhoneOutlined';
import CurrencyExchange from '@mui/icons-material/CurrencyExchangeOutlined';
import DarkModeIcon from '@mui/icons-material/DarkModeOutlined';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import ElectricBoltIcon from '@mui/icons-material/ElectricBoltOutlined';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMicOutlined';
import HelpIcon from '@mui/icons-material/HelpOutlined';
import HubIcon from '@mui/icons-material/HubOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightModeIcon from '@mui/icons-material/LightModeOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOnOutlined';
import LockIcon from '@mui/icons-material/LockOutlined';
import LogoutRoundedIcon from '@mui/icons-material/LogoutOutlined';
import MenuOpenIcon from '@mui/icons-material/MenuOpenOutlined';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServicesOutlined';
import NightsStayIcon from '@mui/icons-material/NightsStayOutlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActiveOutlined';
import NotificationsIcon from '@mui/icons-material/NotificationsOutlined';
import PaidIcon from '@mui/icons-material/PaidOutlined';
import PaletteIcon from '@mui/icons-material/PaletteOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import PlaceIcon from '@mui/icons-material/PlaceOutlined';
import PlayCircleIcon from '@mui/icons-material/PlayCircleOutlined';
import PointOfSaleIcon from '@mui/icons-material/PointOfSaleOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import RoomIcon from '@mui/icons-material/RoomOutlined';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgentOutlined';
import ThunderstormIcon from '@mui/icons-material/ThunderstormOutlined';
import TuneIcon from '@mui/icons-material/TuneOutlined';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUserOutlined';
import WbSunnyIcon from '@mui/icons-material/WbSunnyOutlined';
import {
  alpha,
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Drawer, { drawerClasses } from '@mui/material/Drawer';
import List from '@mui/material/List';
import _ from 'lodash';
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import {
  MODERN_UI_FEATURE,
  THEME_CLASSIC,
  THEME_MODERN,
  THEME_PREFERENCE_KEY,
  writeThemeCookie,
} from '@sophocles/app/themeRouting';
import { NAV_OP, resolveNavLevel } from '@sophocles/components/App/resolveNavLevel';
import { getStaffApps } from '@sophocles/components/App/staffApps';
import UserAvatar from '@sophocles/components/UserAvatar';
import { API_DOMAIN } from '@sophocles/config';
import { getSelectedCustomerSelector } from '@sophocles/features/auth/authSelectors';
import { selectSiteData, selectSiteFeatureAssociationByKey, selectSiteUnit } from '@sophocles/features/entities/site';
import { selectLastMapSearch } from '@sophocles/features/map/slice';
import { activateNav, deactivateNav, toggleNav } from '@sophocles/features/navigation/navigationActions';
import { navigationSelector } from '@sophocles/features/navigation/navigationSelectors';
import {
  selectActiveSectionId,
  selectSettingsContext,
  selectSettingsSections,
} from '@sophocles/features/navigation/settingsNavSelectors';
import { getSelectedPlantSelector, selectedPlant } from '@sophocles/features/sites/slices/selectedPlant/selectors';
import { getSelectedStructure, selectedStructure } from '@sophocles/features/sites/slices/selectedStructure/selectors';
import { selectDisableThirdPartyApi, selectSeparateTenant, selectTenantInfo } from '@sophocles/features/tenant/slice';
import useUserPreference from '@sophocles/hooks/useUserPreference';
import { withRouter } from '@sophocles/router/hoc';
import { customerMapPath, siteGroupLandingPath } from '@sophocles/router/paths';
import tokenService from '@sophocles/services/token';
import { BrandLogo, useColorScheme } from '@sophocles/theme';
import { SMARTLINK_MODEL } from '@sophocles/utils';
import { isEthernet } from '@sophocles/utils/radioUtils';
import { userHasFeature } from '@sophocles/utils/userPermissionUtils';
import { determineSupportsDataReport } from '@sophocles/utils/version';

const SettingsRoundedIcon = SettingsIcon;

// Icon lookup for the settings sidebar.  Pages dispatch sections
// with `icon: <key>` (a string) and the LeftNav resolves the
// component here — keeps the action payload serializable for
// Redux DevTools and avoids passing JSX through the store.
const SETTINGS_NAV_ICONS = {
  person: PersonIcon,
  contactMail: ContactMailIcon,
  notifications: NotificationsIcon,
  notificationsActive: NotificationsActiveIcon,
  lock: LockIcon,
  adminPanelSettings: AdminPanelSettingsIcon,
  // Site settings rail
  info: InfoOutlinedIcon,
  place: PlaceIcon,
  locationOn: LocationOnIcon,
  receiptLong: ReceiptLongIcon,
  cloud: CloudIcon,
  wbSunny: WbSunnyIcon,
  verifiedUser: VerifiedUserIcon,
  paid: PaidIcon,
  tune: TuneIcon,
  // Site group settings rail
  contactPhone: ContactPhoneIcon,
  nightsStay: NightsStayIcon,
  thunderstorm: ThunderstormIcon,
  // Customer settings rail
  attachFile: AttachFileIcon,
  business: BusinessIcon,
  hub: HubIcon,
};

// The staff-app catalog owns permissions and destinations; the sidebar owns
// the visual icon family so both expanded chips and collapsed controls stay
// consistently outlined.
const STAFF_APP_ICONS = {
  admin: AdminPanelSettingsIcon,
  billing: CurrencyExchange,
  services: MiscellaneousServicesIcon,
  sales: PointOfSaleIcon,
  support: SupportAgentIcon,
  returns: AssignmentReturnIcon,
};

/**
 * Convert the snapshot unit's radio.comm_modes object ({cellular, ethernet, wifi})
 * to a plain-JS radio compatible with radioUtils (numeric bitmask comm_modes).
 * When comm_modes is already a number (legacy shape) it passes through unchanged.
 *
 * @param {object|null} contractRadio - unit.radio from the siteDashboard snapshot
 * @returns {object|null}
 */
function normalizeSnapshotRadio(contractRadio) {
  if (contractRadio == null) return null;
  const cm = contractRadio.comm_modes;
  const bitmask =
    cm == null
      ? 0
      : typeof cm === 'number'
        ? cm
        : (cm.cellular ? 0b001 : 0) | (cm.ethernet ? 0b010 : 0) | (cm.wifi ? 0b100 : 0);
  return { ...contractRadio, comm_modes: bitmask };
}

// Modern sub-nav item
function NavItem({ text, active, onNav, pillText, icon }) {
  const theme = useTheme();

  return (
    <ListItemButton
      onClick={onNav}
      aria-current={active ? 'page' : undefined}
      sx={{
        ml: 1.5,
        mr: 1,
        mb: 0.25,
        py: 0.75,
        borderRadius: 'var(--sidebar-item-radius)',
        minHeight: 36,
        '--list-item-primary-color': active ? 'var(--sidebar-item-active-text)' : 'var(--sidebar-text)',
        '--list-item-primary-weight': active ? 'var(--sidebar-item-active-weight)' : 'var(--sidebar-item-weight)',
        color: active ? 'var(--sidebar-item-active-text)' : 'var(--sidebar-text)',
        bgcolor: active ? 'var(--sidebar-item-active)' : 'transparent',
        '&:hover': {
          bgcolor: active ? 'var(--sidebar-item-active)' : 'var(--sidebar-item-hover)',
          color: active ? 'var(--sidebar-item-active-text)' : 'var(--sidebar-text-active)',
        },
        '&.Mui-focusVisible': { boxShadow: 'var(--shadow-focus)', outline: 'none' },
      }}
    >
      {icon ? (
        <ListItemIcon
          sx={{
            width: 'var(--space-8)',
            minWidth: 'var(--space-8)',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'inherit',
            '& svg': { fontSize: 'var(--icon-size)' },
          }}
        >
          {icon}
        </ListItemIcon>
      ) : null}
      <ListItemText
        primary={text}
        primaryTypographyProps={{
          fontSize: 'var(--font-sm)',
        }}
      />
      {pillText && (
        <Chip
          label={pillText}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.625rem',
            fontWeight: 700,
            // Brighter tint so the pill reads against the light sidebar bg
            // (var(--md-surface-container) ≈ #EDEDF4). 10% primary was
            // nearly invisible against that surface.
            bgcolor: alpha(theme.palette.primary.main, 0.18),
            color: 'var(--md-on-primary-container)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      )}
    </ListItemButton>
  );
}

// The selected customer's logo is the sidebar's strongest cue to which tenant
// the session is in, so it takes real room in the expanded rail. The collapsed
// rail is 64px wide before its item padding, which caps what fits there.
const CONTEXT_LOGO_HEIGHT = 30;
const CONTEXT_LOGO_MAX_WIDTH = 84;
const CONTEXT_LOGO_HEIGHT_COLLAPSED = 24;
const CONTEXT_LOGO_MAX_WIDTH_COLLAPSED = 36;

// Context item (Customer, Plant, Structure) with secondary text and optional settings gear
function NavContextItem({
  icon: Icon,
  logoUrl,
  primary,
  secondary,
  active,
  onClick,
  onCollapsedClick,
  onSettingsClick,
  onBillingClick,
  collapsed,
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  // A customer's uploaded logo (when present) takes the icon slot. Logos
  // are often wide/landscape, so render the image height-capped at its
  // natural aspect ratio rather than a square icon; the passed Icon stays
  // the fallback.
  const [logoFailed, setLogoFailed] = React.useState(false);
  const showLogo = Boolean(logoUrl) && !logoFailed;

  const button = (
    <ListItemButton
      onClick={collapsed && onCollapsedClick ? onCollapsedClick : onClick}
      aria-current={active ? 'page' : undefined}
      sx={{
        ml: collapsed ? 0.5 : 1,
        mr: collapsed ? 0.5 : 1,
        mb: 0.5,
        borderRadius: 'var(--sidebar-item-radius)',
        minHeight: collapsed ? 44 : 52,
        justifyContent: collapsed ? 'center' : 'flex-start',
        // Asymmetric padding centers the context icon over the nested
        // subnav rule (the ml: 3 + borderLeft List below).
        pl: collapsed ? 1 : 0.5,
        pr: collapsed ? 1 : 1.5,
        '--list-item-primary-color': active ? 'var(--sidebar-item-active-text)' : 'var(--sidebar-text-active)',
        '--list-item-primary-weight': active ? 'var(--sidebar-item-active-weight)' : 'var(--weight-semibold)',
        color: active ? 'var(--sidebar-item-active-text)' : 'var(--sidebar-text-active)',
        bgcolor: active ? 'var(--sidebar-context-active)' : 'transparent',
        '&:hover': {
          bgcolor: active ? 'var(--sidebar-context-active)' : 'var(--sidebar-item-hover)',
        },
        '&.Mui-focusVisible': { boxShadow: 'var(--shadow-focus)', outline: 'none' },
      }}
    >
      {showLogo ? (
        <ListItemIcon sx={{ minWidth: collapsed ? 0 : 28, mr: collapsed ? 0 : 1, justifyContent: 'center' }}>
          <Box
            component="img"
            src={logoUrl}
            alt=""
            onError={() => setLogoFailed(true)}
            sx={{
              height: collapsed ? CONTEXT_LOGO_HEIGHT_COLLAPSED : CONTEXT_LOGO_HEIGHT,
              width: 'auto',
              maxWidth: collapsed ? CONTEXT_LOGO_MAX_WIDTH_COLLAPSED : CONTEXT_LOGO_MAX_WIDTH,
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </ListItemIcon>
      ) : (
        Icon && (
          <ListItemIcon
            sx={{
              minWidth: collapsed ? 0 : 'calc(var(--sidebar-context-icon-size) + var(--space-2))',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: 'var(--sidebar-context-icon-size)',
                height: 'var(--sidebar-context-icon-size)',
                flexShrink: 0,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 'var(--radius-sm)',
                bgcolor: active ? 'var(--sidebar-item-active)' : 'var(--sidebar-control-bg)',
              }}
            >
              <Icon
                sx={{
                  fontSize: 'var(--icon-size)',
                  color: active ? 'var(--sidebar-item-active-text)' : 'var(--sidebar-text-muted)',
                }}
              />
            </Box>
          </ListItemIcon>
        )
      )}
      {!collapsed && (
        <>
          <ListItemText
            primary={primary}
            secondary={secondary}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              noWrap: true,
            }}
            secondaryTypographyProps={{
              fontSize: '0.75rem',
              color: 'var(--sidebar-text-muted)',
              noWrap: true,
            }}
          />
          {onBillingClick && (
            <Tooltip title={t('general.billing', 'Billing')}>
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  onBillingClick();
                }}
                sx={{
                  color: 'var(--sidebar-text-muted)',
                  '&:hover': { color: 'primary.main', opacity: 1 },
                }}
              >
                <CurrencyExchange sx={{ fontSize: 'var(--icon-size-sm)' }} />
              </IconButton>
            </Tooltip>
          )}
          {onSettingsClick && (
            <Tooltip title={t('general.settings', 'Settings')}>
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  onSettingsClick();
                }}
                sx={{
                  color: 'var(--sidebar-text-muted)',
                  '&:hover': { color: 'primary.main', opacity: 1 },
                }}
              >
                <SettingsRoundedIcon sx={{ fontSize: 'var(--icon-size-sm)' }} />
              </IconButton>
            </Tooltip>
          )}
        </>
      )}
    </ListItemButton>
  );

  if (collapsed) {
    return (
      <Tooltip title={secondary ? `${primary}: ${secondary}` : primary} placement="right">
        {button}
      </Tooltip>
    );
  }

  return button;
}

// Widths for different sidebar states
const SIDEBAR_WIDTH_EXPANDED = 260;
const SIDEBAR_WIDTH_COLLAPSED = 64;

function LeftNav({ page, open, collapsed, onClose, onToggle, onExpand }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [isHovering, setIsHovering] = useState(false);

  const isNativePlatform = Capacitor.isNativePlatform();

  const {
    user,
    isStaff,
    isSuperUser,
    permissions: {
      isReseller,
      canViewTickets,
      canViewUsers,
      canViewRMAs: canViewRMAsPerm,
      isCrewManager,
      isTechnician,
      isAccounting,
      isShipping,
      isSales,
      isSalesAdmin,
      isODLEngineering,
    },
  } = useUser();

  const tenantInfo = useSelector(selectTenantInfo);
  const disableThirdPartyApi = useSelector(selectDisableThirdPartyApi);
  const separateTenant = useSelector(selectSeparateTenant);
  const structureId = useSelector(selectedStructure);
  const structure = useSelector(getSelectedStructure);
  // Snapshot unit from the new site dashboard slice.  Present only when the
  // dashboard route has loaded the snapshot for this site; null on all other
  // routes (charting, calendar, statushist, etc.) where the legacy structure
  // store is used instead.
  const snapshotUnit = useSelector(selectSiteUnit(structureId));
  const snapshotSite = useSelector(selectSiteData(structureId));
  const plant = useSelector(getSelectedPlantSelector);
  const plantId = useSelector(selectedPlant);
  const customer = useSelector(getSelectedCustomerSelector);

  const navigation = useSelector(navigationSelector);
  const settingsSections = useSelector(selectSettingsSections);
  const activeSectionId = useSelector(selectActiveSectionId);
  const settingsContext = useSelector(selectSettingsContext);

  // Below the custom `mobile` (768px) breakpoint the nav is a dismissable
  // overlay; this must match AppNav's own `isMobile` check so the two never
  // disagree (a mismatched band used to render a persistent, undismissable
  // drawer over the page). Both read the same theme breakpoint — no windowSizes.
  const isPhone = useMediaQuery(theme => theme.breakpoints.down('mobile'));

  // Active staff app — persists across navigation.
  //
  // Backed by sessionStorage because LeftNav is remounted as the user
  // moves between page wrappers (SupportApp owns its own LeftNav;
  // AppNav-wrapped pages mount a separate one), so plain useState
  // would reset to null and collapse the submenu every time the user
  // clicked their customer or any non-staff page.
  const [activeStaffApp, setActiveStaffApp] = useState(() => {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage.getItem('leftNav.activeStaffApp') || null;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (activeStaffApp) {
      window.sessionStorage.setItem('leftNav.activeStaffApp', activeStaffApp);
    } else {
      window.sessionStorage.removeItem('leftNav.activeStaffApp');
    }
  }, [activeStaffApp]);

  // Auto-select staff app when navigating to a staff page
  useEffect(() => {
    const staffPageMap = {
      admin: 'admin',
      billing: 'billing',
      services: 'services',
      sales: 'sales',
      support: 'support',
      bluetooth: 'support',
    };
    for (const [prefix, appId] of Object.entries(staffPageMap)) {
      if (_.startsWith(page, prefix)) {
        setActiveStaffApp(appId);
        return;
      }
    }
  }, [page]);

  // User info
  const firstName = user.get('first_name', '');
  const lastName = user.get('last_name', '');
  const email = user.get('email', '');

  // Theme preference + cookie sync
  const [themePref, setThemePref, , flushThemePref] = useUserPreference(THEME_PREFERENCE_KEY, THEME_MODERN);
  // Color scheme (light/dark/system).  Distinct from the modern/classic
  // template selector above — this only controls the M3 palette.
  const { preference: schemePref, scheme: activeScheme, setPreference: setSchemePref } = useColorScheme();
  const handleSchemeToggle = () => {
    // Cycle light → dark → system → light.
    const next = schemePref === 'light' ? 'dark' : schemePref === 'dark' ? 'system' : 'light';
    setSchemePref(next);
  };
  const SchemeIcon = schemePref === 'light' ? LightModeIcon : schemePref === 'dark' ? DarkModeIcon : BrightnessAutoIcon;
  const schemeTooltip =
    schemePref === 'light'
      ? 'Light mode — click for dark'
      : schemePref === 'dark'
        ? 'Dark mode — click for system'
        : `System (${activeScheme}) — click for light`;
  const handleThemeToggle = async () => {
    const next = themePref === THEME_MODERN ? THEME_CLASSIC : THEME_MODERN;
    setThemePref(next);
    writeThemeCookie(next);
    // Ensure the debounced PUT actually goes out before the reload cancels it.
    await flushThemePref();
    window.location.reload();
  };

  // Help menu state
  const [helpMenu, setHelpMenu] = useState(null);
  const openHelpMenu = Boolean(helpMenu);
  const handleHelpClick = event => {
    setHelpMenu(event.currentTarget);
  };
  const handleHelpClose = () => {
    setHelpMenu(null);
  };

  // Impersonation state
  const [hoverImpersonate, setHoverImpersonate] = useState(false);

  const handleToggleNav = level => {
    return dispatch(toggleNav(level));
  };

  const activateNavLevel = level => {
    return dispatch(activateNav(level));
  };

  const deactivateNavLevel = level => {
    return dispatch(deactivateNav(level));
  };

  useEffect(() => {
    resolveNavLevel(location.pathname, page).forEach(({ action, level }) => {
      if (action === NAV_OP.ACTIVATE) {
        activateNavLevel(level);
      } else {
        deactivateNavLevel(level);
      }
    });
  }, [page, location.pathname]);

  // On phone the nav is a temporary overlay; dismiss it once the user
  // navigates (taps any item) so it doesn't sit on top of the page.
  // Keyed on pathname only — adding `open`/`isPhone` would re-close the
  // drawer the instant the user opens it.
  useEffect(() => {
    if (isPhone && open) {
      onClose();
    }
  }, [location.pathname]);

  const canViewRMAs = isStaff || canViewRMAsPerm;
  const isSalesOrShipping = isShipping || isSales;

  const customerId = customer.get('id', '0');
  const customerOptions = customer.get('options', null);
  const customerLinkPrefix = `/c/${customerId}`;

  // An inventory group holds stock and never holds a structure, so opening it
  // on the structures grid would always land on an empty list. The same fact
  // withholds the nav items that only describe structures — the structure
  // node itself, the map that plots them and the reports built from them.
  const isInventoryGroup = plant.get('is_inventory', false);
  const groupLandingPath = siteGroupLandingPath({ id: plantId, is_inventory: isInventoryGroup }, customerId);

  // The map link returns the user to the map they left — same group, filters
  // and viewport — rather than a reset one. Nothing is lost when the map has
  // not been opened this session; the link just opens a default map.
  const rememberedMapSearch = useSelector(selectLastMapSearch(customerId));
  const customerMapHref = `${customerMapPath({ cust_id: customerId })}${
    rememberedMapSearch ? `?${rememberedMapSearch}` : ''
  }`;

  // For the settings-mode "Back" button: read IDs off the current URL
  // so we navigate back to a valid parent route even when redux's
  // selected-customer / -plant / -structure state is stale.  Falls
  // back to the redux ids so non-route-driven entry points still work.
  const routeMatch = matchPath(
    { path: '/c/:cust_id/plant/:sitegroup_id/structure/:site_id/*', end: false },
    location.pathname,
  );
  const routePlantMatch = matchPath({ path: '/c/:cust_id/plant/:sitegroup_id/*', end: false }, location.pathname);
  const routeCustomerMatch = matchPath({ path: '/c/:cust_id/*', end: false }, location.pathname);
  const backCustomerId =
    routeMatch?.params?.cust_id ??
    routePlantMatch?.params?.cust_id ??
    routeCustomerMatch?.params?.cust_id ??
    customerId;
  const backPlantId = routeMatch?.params?.sitegroup_id ?? routePlantMatch?.params?.sitegroup_id ?? plantId;
  const backStructureId = routeMatch?.params?.site_id ?? structureId;

  const isBillboard = customer.get('is_billboard', true);
  const chartingEnabled = (customerOptions?.charting_option ?? 0) > 0 && !disableThirdPartyApi;

  const showPlant = plant.get('customer', '0') === customer.get('id', '-1');

  const showStructure =
    showPlant &&
    !isInventoryGroup &&
    structure.has('id') &&
    structure.get('site_groups', '0').indexOf(plant.get('id', 0)) > -1;

  const dashboardsOpen = navigation.get('dashboards', false);
  const customerOpen = navigation.get('customer', false);
  const siteGroupOpen = navigation.get('siteGroup', false);
  const siteOpen = navigation.get('site', false);

  // Relay-controller gate derivations.
  //
  // On the new site dashboard route the legacy structure store is only sparsely
  // seeded (relay_controller is null), so we prefer the snapshot unit when it is
  // available and fall back to the legacy Immutable structure otherwise.
  // snapshotUnit != null  → dashboard route (snapshot loaded for this site)
  // snapshotUnit == null  → legacy routes (charting, calendar, statushist, …)
  //
  // Shape notes:
  //   - supports_v2_protocol and measures_power_consumption_in_watts are FLAT
  //     on relay_controller in the legacy serializer but nest under
  //     firmware_version in the snapshot (schema confirms both fields live on
  //     SmartLinkUnitFirmwareVersion; legacy flattens them).
  //   - radio.comm_modes is a numeric BitField in legacy but an object
  //     {cellular, ethernet, wifi} in the snapshot; normalizeSnapshotRadio()
  //     converts the snapshot shape so radioUtils.isEthernet() works correctly.
  //   - the standalone site CIC is NOT a unit field (snapshot unit.cic is the
  //     RC-level addon CIC, a different entity), so it keys off the snapshot
  //     SITE: snapshot.site.has_standalone_cic when loaded, else legacy site.cic.
  //     It must work when unit == null (a standalone-CIC site need not have a
  //     relay controller).
  const useSnapshotUnit = snapshotUnit != null;

  const relayControllerPresent = useSnapshotUnit ? true : structure.get('relay_controller', null) != null;

  const isRelayMonitorEnabled = useSnapshotUnit
    ? Boolean(snapshotUnit?.relay_monitor?.enabled)
    : Boolean(
        structure.getIn(['relay_controller', 'relay_monitor'], null) &&
          structure.getIn(['relay_controller', 'relay_monitor', 'enabled']),
      );

  const isWifi = useSnapshotUnit
    ? (snapshotUnit?.radio?.mac_address ?? null) != null
    : structure.getIn(['relay_controller', 'radio', 'mac_address'], null) != null;

  const isVantage = useSnapshotUnit
    ? (snapshotUnit?.model?.id ?? null) == SMARTLINK_MODEL.VANTAGE
    : structure.getIn(['relay_controller', 'model', 'id'], null) == SMARTLINK_MODEL.VANTAGE;

  const supportsV2Protocol = useSnapshotUnit
    ? (snapshotUnit?.firmware_version?.supports_v2_protocol ?? false)
    : structure.getIn(['relay_controller', 'supports_v2_protocol'], false);

  const measuresPower = useSnapshotUnit
    ? (snapshotUnit?.firmware_version?.measures_power_consumption_in_watts ?? null)
    : structure.getIn(['relay_controller', 'measures_power_consumption_in_watts'], null);

  const firmwareVersion = useSnapshotUnit
    ? (snapshotUnit?.firmware_version?.version ?? null)
    : structure.getIn(['relay_controller', 'firmware_version', 'version'], null);

  // radioForUtils: snapshot radio is normalized to a bitmask-based plain object;
  // legacy radio is already the Immutable node that radioUtils handles via .get().
  const radioForUtils = useSnapshotUnit
    ? normalizeSnapshotRadio(snapshotUnit?.radio)
    : structure.getIn(['relay_controller', 'radio'], null);

  const hasGenericStatuses =
    supportsV2Protocol || isWifi || isEthernet(radioForUtils) || determineSupportsDataReport(firmwareVersion);

  // hasStandaloneCic — the SITE-level standalone CIC (site.cic 1->1 Site), which
  // can exist even when the device has no relay controller (unit == null), so it
  // keys off the snapshot SITE rather than the unit: read site.has_standalone_cic
  // when the snapshot site is loaded, else fall back to the legacy site.cic.
  const hasStandaloneCic =
    snapshotSite != null ? Boolean(snapshotSite.has_standalone_cic) : structure.get('cic', null) != null;
  // Gate on the site snapshot's CALENDAR feature association (populated by the
  // site retrieve into siteMeta), keyed by the route's site id — the billboard
  // structureId is stale on the site-tier routes and the legacy featureFlags
  // store the old gate read isn't loaded outside power-data / settings.
  const siteUsesCalendar = useSelector(
    selectSiteFeatureAssociationByKey(routeMatch?.params?.site_id ?? structureId, 'CALENDAR'),
  )?.is_effectively_enabled;
  const showCalendar = siteUsesCalendar;

  // Check if any staff sections should show
  const showStaffSections =
    (isStaff && !isNativePlatform && (isAccounting || isCrewManager)) ||
    (!isNativePlatform && (isAccounting || isSuperUser)) ||
    isCrewManager ||
    isAccounting ||
    isTechnician ||
    (isStaff && isSalesOrShipping) ||
    isStaff ||
    isReseller;

  const renderFrontEndVersion = () => {
    const feVersion = process.env.VERSION;
    return `${t('general.version')}: ${feVersion}`;
  };

  // On desktop, drawer is always "open" but width changes between collapsed and expanded
  // On mobile, drawer is a temporary overlay
  const isCollapsedMode = !isPhone && collapsed && open;
  const drawerWidth = isPhone ? 280 : isCollapsedMode ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;

  return (
    <>
      <Drawer
        id="LeftNav"
        variant={isPhone ? 'temporary' : 'persistent'}
        anchor="left"
        open={open}
        onClose={onClose}
        onMouseEnter={() => !isPhone && setIsHovering(true)}
        onMouseLeave={() => !isPhone && setIsHovering(false)}
        onFocus={() => !isPhone && setIsHovering(true)}
        onBlur={e => !isPhone && !e.currentTarget.contains(e.relatedTarget) && setIsHovering(false)}
        slotProps={{ paper: { component: 'nav', 'aria-label': t('general.primaryNavigation', 'Primary navigation') } }}
        sx={{
          position: 'relative',
          flexShrink: 0,
          zIndex: theme => theme.zIndex.drawer,
          [`& .${drawerClasses.paper}`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'var(--sidebar-border)',
            boxShadow: 'var(--sidebar-shadow)',
            bgcolor: 'var(--sidebar-bg)',
            color: 'var(--sidebar-text)',
            transition: 'width var(--duration-normal) var(--easing-standard)',
            overflowX: 'hidden',
            paddingTop: isPhone ? 'env(safe-area-inset-top)' : 0,
            paddingBottom: isPhone ? 'env(safe-area-inset-bottom)' : 0,
            paddingLeft: isPhone ? 'env(safe-area-inset-left)' : 0,
          },
        }}
      >
        {/* Header Section - Logo and Collapse */}
        {isCollapsedMode ? (
          <Box
            sx={{
              px: 1,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 56,
            }}
          >
            {isHovering ? (
              <Tooltip title={t('general.expand', 'Expand sidebar')} placement="right">
                <IconButton
                  onClick={onExpand}
                  sx={{
                    width: 32,
                    height: 32,
                    color: 'text.secondary',
                    '&:hover': { bgcolor: 'var(--sidebar-item-hover)' },
                  }}
                >
                  <ChevronRightIcon sx={{ fontSize: 'var(--icon-size-sm)' }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={t('general.home', 'Home')} placement="right">
                <Link to="/" style={{ display: 'flex', alignItems: 'center', height: 32 }}>
                  <BrandLogo
                    size="small"
                    inverse={activeScheme === 'dark'}
                    style={{ height: 32, width: 32, objectFit: 'contain' }}
                  />
                </Link>
              </Tooltip>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: 56,
            }}
          >
            <Link to="/" style={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
              <BrandLogo
                size="large"
                inverse={activeScheme === 'dark'}
                style={{ height: 32, maxWidth: '100%', objectFit: 'contain' }}
              />
            </Link>
            {isPhone ? (
              <IconButton
                size="small"
                onClick={onClose}
                aria-label={t('general.close', 'Close')}
                sx={{ color: 'text.secondary', ml: 1, flexShrink: 0 }}
              >
                <CloseIcon sx={{ fontSize: 'var(--icon-size-sm)' }} />
              </IconButton>
            ) : (
              <IconButton size="small" onClick={onToggle} sx={{ color: 'text.secondary', ml: 1, flexShrink: 0 }}>
                <MenuOpenIcon sx={{ fontSize: 'var(--icon-size-sm)' }} />
              </IconButton>
            )}
          </Box>
        )}

        <Stack
          direction="column"
          sx={{
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {/* Impersonation Warning - hidden when collapsed */}
          {tokenService.isImpersonating && !isCollapsedMode && (
            <Box sx={{ px: 2, py: 1 }}>
              <Chip
                fullWidth
                color="warning"
                label={hoverImpersonate ? 'Stop Impersonating' : 'Impersonation Mode'}
                onMouseEnter={() => setHoverImpersonate(true)}
                onMouseLeave={() => setHoverImpersonate(false)}
                onClick={() => {
                  tokenService.stopImpersonating();
                  window.location.reload();
                }}
                sx={{ width: '100%' }}
              />
            </Box>
          )}
          {/* Scrollable nav content */}
          <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1 }}>
            {/* Settings Mode */}
            {settingsContext && !isCollapsedMode && (
              <Box>
                {/* Back button */}
                <ListItemButton
                  onClick={() => {
                    if (settingsContext.context === 'user') {
                      navigate(-1);
                    } else {
                      // Keyed by the `contextName` each settings page declares
                      // — `site_group`, not `siteGroup`. A key that misses
                      // falls through to the root, which is how leaving a
                      // plant's settings used to land on the plant list.
                      const contextPaths = {
                        customer: `/c/${backCustomerId}/plants`,
                        site_group: `/c/${backCustomerId}/plant/${backPlantId}/structures`,
                        site: `/c/${backCustomerId}/plant/${backPlantId}/structure/${backStructureId}`,
                      };
                      navigate(contextPaths[settingsContext.context] || '/');
                    }
                  }}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1.5,
                    minHeight: 40,
                    px: 2,
                    color: 'text.secondary',
                    '&:hover': { bgcolor: 'var(--sidebar-item-hover)', color: 'text.primary' },
                    '&.Mui-focusVisible': { boxShadow: 'var(--shadow-focus)', outline: 'none' },
                  }}
                >
                  <ListItemIcon sx={{ width: 'var(--space-8)', minWidth: 'var(--space-8)', justifyContent: 'center' }}>
                    <ArrowBackIcon sx={{ fontSize: 'var(--icon-size-sm)' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('general.back', 'Back')}
                    primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: 500 }}
                  />
                </ListItemButton>

                {/* Settings heading */}
                <Typography
                  variant="overline"
                  sx={{
                    px: 3,
                    py: 1,
                    display: 'block',
                    color: 'text.secondary',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                  }}
                >
                  {t('general.settings')}
                </Typography>

                {/* Settings section links.  Pages can opt into a
                    scroll-jump mode (single-page settings layouts) by
                    dispatching `setSettingsContext({ ..., scroll: true })`
                    — clicking a section then scrollIntoView's the
                    matching DOM id instead of route-navigating.
                    `section.icon` is an optional key into
                    SETTINGS_NAV_ICONS for an icon glyph next to the
                    title. */}
                <List disablePadding>
                  {settingsSections.map(section => {
                    const IconComponent = section.icon ? SETTINGS_NAV_ICONS[section.icon] : null;
                    return (
                      <NavItem
                        key={section.id}
                        text={section.title}
                        active={activeSectionId === section.id}
                        icon={IconComponent ? <IconComponent /> : null}
                        pillText={section.pillText}
                        onNav={() => {
                          if (settingsContext.scroll) {
                            const el = document.getElementById(section.id);
                            if (el) {
                              el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                            // Mirror the section into the URL so the link is
                            // copy/pasteable. `replace:true` keeps the back
                            // button pointing at the prior page instead of
                            // accumulating an entry per scroll-target.
                            const slug = section.id.replace(/^settings-/, '');
                            navigate(`${settingsContext.basePath}/settings/${slug}`, { replace: true });
                          } else {
                            navigate(`${settingsContext.basePath}/${section.id}`);
                          }
                        }}
                      />
                    );
                  })}
                </List>
              </Box>
            )}

            {/* Normal nav (hidden when in settings mode on expanded sidebar) */}
            {(!settingsContext || isCollapsedMode) && (
              <List disablePadding>
                {/* Search — all authenticated users (manual ICCID lookup on
                    web; manual + BLE proximity on native) */}
                {!isCollapsedMode && (
                  <ListItemButton
                    onClick={() => navigate('/search')}
                    sx={{
                      mx: 1.5,
                      mt: 0.5,
                      mb: 1,
                      px: 1.25,
                      py: 0.75,
                      gap: 1,
                      borderRadius: 'var(--sidebar-item-radius)',
                      border: '1px solid',
                      borderColor:
                        page === 'search' ? 'var(--sidebar-search-active-border)' : 'var(--sidebar-search-border)',
                      '--list-item-primary-color':
                        page === 'search' ? 'var(--sidebar-item-active-text)' : 'var(--sidebar-text-active)',
                      '--list-item-primary-weight':
                        page === 'search' ? 'var(--sidebar-item-active-weight)' : 'var(--weight-semibold)',
                      color: page === 'search' ? 'var(--sidebar-item-active-text)' : 'var(--sidebar-text-active)',
                      bgcolor: page === 'search' ? 'var(--sidebar-search-active-bg)' : 'var(--sidebar-search-bg)',
                      boxShadow: 'var(--sidebar-search-shadow)',
                      transition:
                        'background-color var(--duration-fast) var(--easing-standard), border-color var(--duration-fast) var(--easing-standard), box-shadow var(--duration-fast) var(--easing-standard)',
                      '&:hover': {
                        bgcolor: page === 'search' ? 'var(--sidebar-search-active-bg)' : 'var(--sidebar-search-bg)',
                        borderColor:
                          page === 'search'
                            ? 'var(--sidebar-search-active-border)'
                            : 'var(--sidebar-search-border-hover)',
                        color: page === 'search' ? 'var(--sidebar-item-active-text)' : 'var(--sidebar-text-active)',
                      },
                      '&.Mui-focusVisible': { boxShadow: 'var(--shadow-focus)', outline: 'none' },
                    }}
                  >
                    <SearchIcon
                      sx={{
                        fontSize: 'var(--icon-size)',
                        color: page === 'search' ? 'var(--sidebar-item-active-text)' : 'var(--sidebar-search-icon)',
                      }}
                    />
                    <ListItemText
                      primary={`${t('search.navLabel', 'Search')}…`}
                      primaryTypographyProps={{ fontSize: '0.8125rem' }}
                    />
                  </ListItemButton>
                )}
                {isCollapsedMode && (
                  <Tooltip title={t('search.navLabel', 'Search')} placement="right">
                    <ListItemButton
                      onClick={() => navigate('/search')}
                      sx={{
                        mx: 0.5,
                        mb: 0.5,
                        borderRadius: 'var(--button-radius)',
                        minHeight: 44,
                        justifyContent: 'center',
                        px: 1,
                        border: '1px solid',
                        borderColor:
                          page === 'search' ? 'var(--sidebar-search-active-border)' : 'var(--sidebar-search-border)',
                        color: page === 'search' ? 'var(--sidebar-item-active-text)' : 'var(--sidebar-search-icon)',
                        bgcolor: page === 'search' ? 'var(--sidebar-search-active-bg)' : 'var(--sidebar-search-bg)',
                        boxShadow: 'var(--sidebar-search-shadow)',
                        '&:hover': {
                          bgcolor: page === 'search' ? 'var(--sidebar-search-active-bg)' : 'var(--sidebar-search-bg)',
                          borderColor:
                            page === 'search'
                              ? 'var(--sidebar-search-active-border)'
                              : 'var(--sidebar-search-border-hover)',
                        },
                        '&.Mui-focusVisible': { boxShadow: 'var(--shadow-focus)', outline: 'none' },
                      }}
                    >
                      <SearchIcon sx={{ fontSize: 'var(--icon-size)' }} />
                    </ListItemButton>
                  </Tooltip>
                )}
                {/* Staff App Switcher */}
                {showStaffSections &&
                  !isCollapsedMode &&
                  (() => {
                    const staffApps = getStaffApps(
                      {
                        isStaff,
                        isNativePlatform,
                        isAccounting,
                        isCrewManager,
                        isSuperUser,
                        isTechnician,
                        isSalesOrShipping,
                        isReseller,
                      },
                      t,
                    );

                    if (staffApps.length === 0) return null;

                    return (
                      <>
                        {/* Staff app chips */}
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                            gridAutoRows: 'minmax(var(--space-8), auto)',
                            gap: 'var(--space-1)',
                            mx: 'var(--space-3)',
                            my: 'var(--space-2)',
                            p: 'var(--space-1)',
                            borderRadius: 'var(--radius-lg)',
                            bgcolor: 'var(--sidebar-app-tray-bg)',
                            boxShadow: 'var(--sidebar-app-tray-shadow)',
                          }}
                        >
                          {staffApps.map(app => {
                            const isActive = activeStaffApp === app.id;
                            const AppIcon = STAFF_APP_ICONS[app.id] || app.icon;
                            return (
                              <Chip
                                key={app.id}
                                icon={<AppIcon sx={{ fontSize: 'var(--sidebar-app-icon-size) !important' }} />}
                                label={app.label}
                                size="small"
                                onClick={() => setActiveStaffApp(app.id)}
                                sx={{
                                  width: '100%',
                                  minWidth: 0,
                                  height: 'auto',
                                  minHeight: 'var(--space-8)',
                                  justifyContent: 'center',
                                  fontSize: 'var(--sidebar-app-font-size)',
                                  lineHeight: 1.1,
                                  fontWeight: isActive
                                    ? 'var(--sidebar-item-active-weight)'
                                    : 'var(--sidebar-item-weight)',
                                  color: isActive ? 'var(--sidebar-item-active-text)' : 'var(--sidebar-text)',
                                  bgcolor: isActive ? 'var(--sidebar-item-active)' : 'transparent',
                                  border: 0,
                                  borderRadius: 'var(--sidebar-item-radius)',
                                  boxShadow: 'none',
                                  '& .MuiChip-icon': {
                                    flexShrink: 0,
                                    ml: 'var(--space-1)',
                                    mr: 0,
                                    color: isActive ? 'var(--sidebar-item-active-text)' : 'var(--sidebar-text-muted)',
                                  },
                                  '& .MuiChip-label': {
                                    minWidth: 0,
                                    px: 'calc(var(--space-1) / 2)',
                                    py: 'var(--space-1)',
                                    whiteSpace: 'normal',
                                    overflow: 'visible',
                                    textOverflow: 'clip',
                                    overflowWrap: 'anywhere',
                                    textAlign: 'center',
                                    lineHeight: 1.1,
                                  },
                                  '&.Mui-focusVisible': { boxShadow: 'var(--shadow-focus)' },
                                  '&:hover': {
                                    bgcolor: isActive ? 'var(--sidebar-item-active)' : 'var(--sidebar-item-hover)',
                                    boxShadow: 'none',
                                  },
                                }}
                              />
                            );
                          })}
                        </Box>

                        {/* Sub-items for active staff app */}
                        {activeStaffApp && (
                          <Collapse in timeout="auto">
                            <List disablePadding sx={{ pb: 0.5 }}>
                              {activeStaffApp === 'admin' && (
                                <NavItem
                                  text={t('general.dashboard')}
                                  active={page === 'admin_home'}
                                  onNav={() => navigate('/staff')}
                                />
                              )}
                              {!__EXCLUDE_RETURNS__ && activeStaffApp === 'returns' && (
                                <NavItem
                                  text={t('general.rmas', 'RMAs')}
                                  active={page === 'returns_rmas'}
                                  onNav={() => navigate('/rmas')}
                                />
                              )}
                              {!__EXCLUDE_BILLING__ && activeStaffApp === 'billing' && (
                                <>
                                  <NavItem
                                    text={t('billing.accounts', 'Accounts')}
                                    active={page === 'billing_accounts' || page === 'billing_account'}
                                    onNav={() => navigate('/billing/accounts')}
                                  />
                                  <NavItem
                                    text={t('billing.prepaidTracking', 'Prepaid Tracking')}
                                    active={page === 'billing_admin_prepaid-agreements'}
                                    onNav={() => navigate('/billing/prepaid-tracking')}
                                  />
                                  <NavItem
                                    text={t('billing.trialUnits', 'Trial Units')}
                                    active={page === 'billing_reports'}
                                    onNav={() => navigate('/billing/trial-units')}
                                  />
                                </>
                              )}
                              {!__EXCLUDE_SERVICES__ && activeStaffApp === 'services' && (
                                <>
                                  <NavItem
                                    text={t('general.dashboard')}
                                    active={page === 'services_dashboard'}
                                    onNav={() => navigate('/services/')}
                                  />
                                  <NavItem
                                    text={t('general.projects')}
                                    active={typeof page == 'string' && page.startsWith('services_projects')}
                                    onNav={() => navigate('/services/projects')}
                                  />
                                  {(isCrewManager || isAccounting || isSuperUser) && (
                                    <NavItem
                                      text={t('general.crews')}
                                      active={typeof page == 'string' && page.startsWith('services_crews')}
                                      onNav={() => navigate('/services/crews')}
                                    />
                                  )}
                                </>
                              )}
                              {!__EXCLUDE_SALES__ && activeStaffApp === 'sales' && (
                                <>
                                  <NavItem
                                    text={t('general.orders')}
                                    active={page === 'sales_orders'}
                                    onNav={() => navigate('/sales/orders')}
                                  />
                                  <NavItem
                                    text={t('general.products')}
                                    active={page === 'sales_products'}
                                    onNav={() => navigate('/sales/products')}
                                  />
                                  {isSalesAdmin && (
                                    <NavItem
                                      text={t('general.salesPeople')}
                                      pillText={t('general.admin')}
                                      active={page === 'sales_people'}
                                      onNav={() => navigate('/sales/people')}
                                    />
                                  )}
                                </>
                              )}
                              {activeStaffApp === 'support' && (
                                <>
                                  <NavItem
                                    text={t('customer.customers')}
                                    active={page === 'support_customers'}
                                    onNav={() => navigate('/support/customers')}
                                  />
                                  {!isNativePlatform && (
                                    <NavItem
                                      text={t('userSettings.userManagement')}
                                      active={page === 'support_users'}
                                      onNav={() => navigate('/support/users')}
                                    />
                                  )}
                                  {isStaff && isNativePlatform && (
                                    <NavItem
                                      icon={<BluetoothIcon />}
                                      text={t('bluetooth.scanPage.navLabel', 'Bluetooth Scan')}
                                      pillText={t('general.staff', 'Staff')}
                                      active={page === 'bluetooth_scan'}
                                      onNav={() => navigate('/bluetooth/scan')}
                                    />
                                  )}
                                </>
                              )}
                            </List>
                          </Collapse>
                        )}

                        {/* Divider between staff sections and context sections */}
                        {!customer.isEmpty() && (
                          <Box sx={{ my: 0.5, mx: 2, borderTop: '1px solid', borderColor: 'var(--sidebar-divider)' }} />
                        )}
                      </>
                    );
                  })()}

                {/* Collapsed sidebar: show staff icons stacked */}
                {showStaffSections &&
                  isCollapsedMode &&
                  (() => {
                    const staffApps = getStaffApps(
                      {
                        isStaff,
                        isNativePlatform,
                        isAccounting,
                        isCrewManager,
                        isSuperUser,
                        isTechnician,
                        isSalesOrShipping,
                        isReseller,
                      },
                      t,
                    );

                    return staffApps.map(app => {
                      const AppIcon = STAFF_APP_ICONS[app.id] || app.icon;
                      return (
                        <Tooltip key={app.id} title={app.label} placement="right">
                          <ListItemButton
                            onClick={() => navigate(app.path)}
                            sx={{
                              mx: 0.5,
                              mb: 0.5,
                              borderRadius: 'var(--button-radius)',
                              minHeight: 44,
                              justifyContent: 'center',
                              px: 1,
                              color:
                                activeStaffApp === app.id
                                  ? 'var(--sidebar-item-active-text)'
                                  : 'var(--sidebar-text-muted)',
                              bgcolor: activeStaffApp === app.id ? 'var(--sidebar-item-active)' : 'transparent',
                              '&:hover': {
                                bgcolor: 'var(--sidebar-item-hover)',
                              },
                              '&.Mui-focusVisible': { boxShadow: 'var(--shadow-focus)', outline: 'none' },
                            }}
                          >
                            <AppIcon sx={{ fontSize: 'var(--icon-size)' }} />
                          </ListItemButton>
                        </Tooltip>
                      );
                    });
                  })()}

                {/* Context Sections - Customer, Plant, Structure */}
                {!customer.isEmpty() && (
                  <Fragment>
                    {(() => {
                      const billingAccountId = customer.get('billing_account_id', null);
                      // Match develop's legacy CustomerTitleItems gate: accounting only,
                      // hidden on native, and only when the customer actually has a
                      // billing account.
                      const canSeeBilling = isAccounting && !isNativePlatform && billingAccountId != null;
                      return (
                        <NavContextItem
                          icon={AccountBalanceIcon}
                          logoUrl={customer.get('logo')}
                          primary={customer.get('name') || t('general.noneSelected', 'None selected')}
                          active={_.startsWith(page, 'customer')}
                          expanded={customerOpen}
                          hasChildren
                          collapsed={isCollapsedMode}
                          onClick={() => navigate(`${customerLinkPrefix}/plants`)}
                          onCollapsedClick={() => navigate(`${customerLinkPrefix}/plants`)}
                          onToggleChildren={() => handleToggleNav('customer')}
                          onSettingsClick={() => navigate(`${customerLinkPrefix}/settings`)}
                          onBillingClick={
                            !__EXCLUDE_BILLING__ && canSeeBilling
                              ? () => navigate(`/billing/accounts/${billingAccountId}`)
                              : undefined
                          }
                        />
                      );
                    })()}
                    <Collapse in={customerOpen && !isCollapsedMode} timeout="auto" unmountOnExit>
                      <List
                        disablePadding
                        sx={{
                          pb: 0.5,
                          ml: 3,
                          borderLeft: '1.5px solid',
                          borderColor: 'var(--sidebar-nest-rule)',
                        }}
                      >
                        {isBillboard && (
                          <>
                            <NavItem
                              text={t('general.dashboard', 'Dashboard')}
                              active={_.startsWith(page, 'customer_') && page.endsWith('_dashboard')}
                              onNav={() => navigate(`${customerLinkPrefix}/dashboards/operations`)}
                            />
                            {isODLEngineering && (
                              <Collapse in={dashboardsOpen} timeout="auto" unmountOnExit>
                                <List
                                  disablePadding
                                  sx={{
                                    ml: 1.5,
                                  }}
                                >
                                  <NavItem
                                    text={t('general.management', 'Management')}
                                    active={page === 'customer_management_dashboard'}
                                    onNav={() => navigate(`${customerLinkPrefix}/dashboards/management`)}
                                  />
                                </List>
                              </Collapse>
                            )}
                          </>
                        )}
                        <NavItem
                          text={t('general.map', 'Map')}
                          active={page === 'customer_map'}
                          onNav={() => navigate(customerMapHref)}
                        />
                        {!isNativePlatform && (
                          <NavItem
                            text={t('general.reports')}
                            active={_.startsWith(page, 'customer_reports')}
                            onNav={() => navigate(`${customerLinkPrefix}/reports`)}
                          />
                        )}
                        {!isNativePlatform && canViewUsers && (
                          <NavItem
                            text={t('general.users')}
                            active={page === 'customer_users'}
                            onNav={() => navigate(`${customerLinkPrefix}/users`)}
                          />
                        )}
                      </List>
                    </Collapse>
                  </Fragment>
                )}

                {showPlant && (
                  <Fragment>
                    <NavContextItem
                      icon={HubIcon}
                      primary={plant.get('name', '')}
                      secondary={plant.get('description', '') || undefined}
                      active={_.startsWith(page, 'plant')}
                      expanded={siteGroupOpen}
                      hasChildren
                      collapsed={isCollapsedMode}
                      onClick={() => navigate(groupLandingPath)}
                      onCollapsedClick={() => navigate(groupLandingPath)}
                      onToggleChildren={() => handleToggleNav('siteGroup')}
                      onSettingsClick={() => navigate(`${customerLinkPrefix}/plant/${plantId}/settings`)}
                    />
                    <Collapse in={siteGroupOpen && !isCollapsedMode} timeout="auto" unmountOnExit>
                      <List
                        disablePadding
                        sx={{
                          pb: 0.5,
                          ml: 3,
                          borderLeft: '1.5px solid',
                          borderColor: 'var(--sidebar-nest-rule)',
                        }}
                      >
                        {!isInventoryGroup && (
                          <NavItem
                            text={t('general.map', 'Map')}
                            active={location.pathname.startsWith(`${customerLinkPrefix}/plant/${plantId}/map`)}
                            onNav={() => navigate(`${customerLinkPrefix}/plant/${plantId}/map`)}
                          />
                        )}
                        {!isNativePlatform && !isInventoryGroup && (
                          <NavItem
                            text={t('general.reports')}
                            active={location.pathname.startsWith(`${customerLinkPrefix}/plant/${plantId}/reports`)}
                            onNav={() => navigate(`${customerLinkPrefix}/plant/${plantId}/reports`)}
                          />
                        )}
                        {!isNativePlatform && canViewUsers && (
                          <NavItem
                            text={t('general.users')}
                            active={location.pathname.startsWith(`${customerLinkPrefix}/plant/${plantId}/users`)}
                            onNav={() => navigate(`${customerLinkPrefix}/plant/${plantId}/users`)}
                          />
                        )}
                        {/* Plant-context Tickets — staff-only. RMAs — staff or view_rma perm.
                            The route guards do the canonical check; this just hides the nav item. */}
                        {!__EXCLUDE_RETURNS__ && !isNativePlatform && canViewTickets && (
                          <NavItem
                            text={t('general.tickets', 'Tickets')}
                            pillText={t('general.staff', 'Staff')}
                            active={location.pathname.startsWith(
                              `${customerLinkPrefix}/plant/${plantId}/support/tickets`,
                            )}
                            onNav={() => navigate(`${customerLinkPrefix}/plant/${plantId}/support/tickets`)}
                          />
                        )}
                        {!__EXCLUDE_RETURNS__ && !isNativePlatform && canViewRMAs && (
                          <NavItem
                            text={t('general.rmas', 'RMAs')}
                            active={location.pathname.startsWith(`${customerLinkPrefix}/plant/${plantId}/support/rmas`)}
                            onNav={() => navigate(`${customerLinkPrefix}/plant/${plantId}/support/rmas`)}
                          />
                        )}
                      </List>
                    </Collapse>
                  </Fragment>
                )}

                {showStructure && (
                  <Fragment>
                    <NavContextItem
                      icon={RoomIcon}
                      primary={structure.get('name')}
                      secondary={structure.get('description', '') || undefined}
                      active={_.startsWith(page, 'structure')}
                      expanded={siteOpen}
                      hasChildren
                      collapsed={isCollapsedMode}
                      onClick={() => navigate(`${customerLinkPrefix}/plant/${plantId}/structure/${structureId}`)}
                      onCollapsedClick={() =>
                        navigate(`${customerLinkPrefix}/plant/${plantId}/structure/${structureId}`)
                      }
                      onToggleChildren={() => handleToggleNav('site')}
                      onSettingsClick={() =>
                        navigate(`${customerLinkPrefix}/plant/${plantId}/structure/${structureId}/settings`)
                      }
                    />
                    <Collapse in={siteOpen && !isCollapsedMode} timeout="auto" unmountOnExit>
                      <List
                        disablePadding
                        sx={{
                          pb: 0.5,
                          ml: 3,
                          borderLeft: '1.5px solid',
                          borderColor: 'var(--sidebar-nest-rule)',
                        }}
                      >
                        {!isNativePlatform && isRelayMonitorEnabled && (
                          <NavItem
                            text={t('sites.activityLog')}
                            active={page === 'structure_activity_log'}
                            onNav={() =>
                              navigate(`${customerLinkPrefix}/plant/${plantId}/structure/${structureId}/activity-log`)
                            }
                          />
                        )}
                        <NavItem
                          text={t('sites.alarmHistory', 'Alarm History')}
                          active={page === 'structure_alarmhist'}
                          onNav={() =>
                            navigate(`${customerLinkPrefix}/plant/${plantId}/structure/${structureId}/alarmhist`)
                          }
                        />
                        {showCalendar && (
                          <NavItem
                            text={t('general.calendar')}
                            active={page === 'site_calendar'}
                            onNav={() =>
                              navigate(`${customerLinkPrefix}/plant/${plantId}/structure/${structureId}/calendar`)
                            }
                          />
                        )}
                        {chartingEnabled && (
                          <NavItem
                            text={t('sites.charting')}
                            active={page === 'site_charting'}
                            onNav={() =>
                              navigate(`${customerLinkPrefix}/plant/${plantId}/structure/${structureId}/charting`)
                            }
                          />
                        )}
                        <NavItem
                          text={t('sites.commandLog')}
                          active={page === 'structure_command_log'}
                          onNav={() =>
                            navigate(`${customerLinkPrefix}/plant/${plantId}/structure/${structureId}/command-log`)
                          }
                        />
                        <NavItem
                          text={t('sites.maintenanceHistory', 'Maintenance History')}
                          active={page === 'structure_maintenancehist'}
                          onNav={() =>
                            navigate(`${customerLinkPrefix}/plant/${plantId}/structure/${structureId}/maintenancehist`)
                          }
                        />
                        <NavItem
                          text={t('general.media')}
                          active={page === 'site_media'}
                          onNav={() =>
                            navigate(`${customerLinkPrefix}/plant/${plantId}/structure/${structureId}/media`)
                          }
                        />
                        {!isNativePlatform && relayControllerPresent && (
                          <NavItem
                            text={t('sites.proofOfPerformance')}
                            active={page === 'structure_compliance' || page === 'structure_pop'}
                            onNav={() =>
                              navigate(
                                `${customerLinkPrefix}/plant/${plantId}/structure/${structureId}/${supportsV2Protocol ? 'compliance' : 'pop'}`,
                              )
                            }
                          />
                        )}
                        {relayControllerPresent && (
                          <NavItem
                            text={
                              isVantage
                                ? t('sites.readings')
                                : measuresPower
                                  ? t('sites.powerReadings')
                                  : t('sites.currentReadings')
                            }
                            active={page === 'site_power' || page === 'site_measurements'}
                            onNav={() =>
                              navigate(`${customerLinkPrefix}/plant/${plantId}/structure/${structureId}/readings`)
                            }
                          />
                        )}
                        {isStaff && !isNativePlatform && relayControllerPresent && !supportsV2Protocol && (
                          <NavItem
                            text={t('sites.statusHistory')}
                            pillText={t('general.staff', 'Staff')}
                            active={page === 'structure_statushist'}
                            onNav={() =>
                              navigate(`${customerLinkPrefix}/plant/${plantId}/structure/${structureId}/statushist`)
                            }
                          />
                        )}
                        {isStaff && hasStandaloneCic && (
                          <NavItem
                            text={`${t('sites.statusHistory')} (CIC)`}
                            pillText={t('general.staff', 'Staff')}
                            active={page === 'structure_cicstatushist'}
                            onNav={() =>
                              navigate(`${customerLinkPrefix}/plant/${plantId}/structure/${structureId}/cicstatushist`)
                            }
                          />
                        )}
                        {isStaff && !isNativePlatform && hasGenericStatuses && (
                          <NavItem
                            text={supportsV2Protocol ? t('sites.statusHistory') : `${t('sites.statusHistory')} V2`}
                            pillText={t('general.staff', 'Staff')}
                            active={page === 'structure_statuses'}
                            onNav={() =>
                              navigate(`${customerLinkPrefix}/plant/${plantId}/structure/${structureId}/statuses`)
                            }
                          />
                        )}
                      </List>
                    </Collapse>
                  </Fragment>
                )}
              </List>
            )}
          </Box>

          {/* Footer Section - User Info, Help, Settings */}
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: 'var(--sidebar-footer-divider)',
              bgcolor: 'var(--sidebar-bg)',
              boxShadow: 'var(--sidebar-footer-shadow)',
            }}
          >
            {/* Impersonation Warning - hidden when collapsed */}
            {tokenService.isImpersonating && !isCollapsedMode && (
              <Box sx={{ px: 2, py: 1 }}>
                <Chip
                  fullWidth
                  color="warning"
                  label={hoverImpersonate ? 'Stop Impersonating' : 'Impersonation Mode'}
                  onMouseEnter={() => setHoverImpersonate(true)}
                  onMouseLeave={() => setHoverImpersonate(false)}
                  onClick={() => {
                    tokenService.stopImpersonating();
                    window.location.reload();
                  }}
                  sx={{ width: '100%' }}
                />
              </Box>
            )}
            {/* User Info Row */}
            <Box
              sx={{
                px: isCollapsedMode ? 1 : 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: isCollapsedMode ? 'center' : 'flex-start',
                gap: 1.5,
              }}
            >
              <Tooltip title={isCollapsedMode ? `${firstName} ${lastName}` : ''} placement="right">
                <UserAvatar
                  email={email}
                  firstName={firstName}
                  lastName={lastName}
                  avatarUrl={user.get('avatar')}
                  size={36}
                  sx={{ bgcolor: 'primary.main', fontSize: '0.875rem' }}
                />
              </Tooltip>
              {!isCollapsedMode && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ color: 'var(--sidebar-text-active)', fontWeight: 'var(--weight-semibold)' }}
                  >
                    {firstName} {lastName}
                  </Typography>
                  <Typography variant="caption" noWrap sx={{ display: 'block', color: 'var(--sidebar-text-muted)' }}>
                    {email}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Action Buttons Row */}
            <Box
              sx={{
                px: isCollapsedMode ? 1 : 2,
                pb: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: isCollapsedMode ? 'center' : 'flex-start',
                flexDirection: isCollapsedMode ? 'column' : 'row',
                gap: 1,
                '& .MuiIconButton-root': {
                  color: 'var(--sidebar-text-muted)',
                  width: 'var(--space-8)',
                  height: 'var(--space-8)',
                  borderRadius: 'var(--button-radius)',
                  // 44px hit target on touch; desktop keeps the compact size.
                  ...(isPhone && { minWidth: 44, minHeight: 44 }),
                  '&:hover': {
                    color: 'var(--sidebar-text-active)',
                    bgcolor: 'var(--sidebar-item-hover)',
                  },
                  '&.Mui-focusVisible': { boxShadow: 'var(--shadow-focus)', outline: 'none' },
                },
              }}
            >
              <Tooltip title={t('other.getHelp')}>
                <IconButton size="small" onClick={handleHelpClick}>
                  <HelpIcon sx={{ fontSize: 'var(--icon-size-sm)' }} />
                </IconButton>
              </Tooltip>
              <Menu
                id="help-menu"
                anchorEl={helpMenu}
                open={openHelpMenu}
                onClose={handleHelpClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                slotProps={{ paper: { sx: { minWidth: 320 } } }}
              >
                {!separateTenant && [
                  <Box key="help-learn-header" sx={{ px: 2, py: 1 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.5 }}
                    >
                      LEARN
                    </Typography>
                  </Box>,
                  <a
                    key="help-video-playlist"
                    href="https://www.youtube.com/playlist?list=PLWBlDitf13OmB6C0ta9RsO3lTOQiD3gwL"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <MenuItem onClick={handleHelpClose}>
                      <ListItemIcon>
                        <PlayCircleIcon sx={{ fontSize: 'var(--icon-size-sm)' }} />
                      </ListItemIcon>
                      <ListItemText>Portal Video Playlist</ListItemText>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        (Updated 2026)
                      </Typography>
                    </MenuItem>
                  </a>,
                  <a
                    key="help-modem-card-faq"
                    href={`${API_DOMAIN}/static/pliny/pdf/Modem%20Card%20Upgrade%20FAQs%20FINAL.pdf`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <MenuItem onClick={handleHelpClose}>
                      <ListItemIcon>
                        <DownloadIcon sx={{ fontSize: 'var(--icon-size-sm)' }} />
                      </ListItemIcon>
                      <ListItemText>Portal FAQ &amp; Updates</ListItemText>
                    </MenuItem>
                  </a>,
                  <a key="help-alarm-troubleshooting" href="#" target="_blank" rel="noreferrer">
                    <MenuItem onClick={handleHelpClose}>
                      <ListItemIcon>
                        <ElectricBoltIcon sx={{ fontSize: 'var(--icon-size-sm)' }} />
                      </ListItemIcon>
                      <ListItemText>Alarm Troubleshooting Guide</ListItemText>
                    </MenuItem>
                  </a>,
                  <a key="help-docs" href="https://docs.outdoorlinkinc.com" target="_blank" rel="noreferrer">
                    <MenuItem onClick={handleHelpClose}>
                      <ListItemIcon>
                        <ErrorOutlineIcon sx={{ fontSize: 'var(--icon-size-sm)' }} />
                      </ListItemIcon>
                      <ListItemText>Documentation</ListItemText>
                    </MenuItem>
                  </a>,
                  <Divider key="help-learn-divider" />,
                  <Box key="help-support-header" sx={{ px: 2, py: 1 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.5 }}
                    >
                      GET SUPPORT
                    </Typography>
                  </Box>,
                  <a key="help-ticket" href="#" target="_blank" rel="noreferrer">
                    <MenuItem onClick={handleHelpClose}>
                      <ListItemIcon>
                        <HeadsetMicIcon sx={{ fontSize: 'var(--icon-size-sm)' }} />
                      </ListItemIcon>
                      <ListItemText>Submit Help Ticket</ListItemText>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        (Freshdesk)
                      </Typography>
                    </MenuItem>
                  </a>,
                  <a key="help-whatsapp" href="#" target="_blank" rel="noreferrer">
                    <MenuItem onClick={handleHelpClose}>
                      <ListItemIcon>
                        <DescriptionIcon sx={{ fontSize: 'var(--icon-size-sm)' }} />
                      </ListItemIcon>
                      <ListItemText>WhatsApp Support</ListItemText>
                    </MenuItem>
                  </a>,
                ]}
                {tenantInfo.contact_email != null && (
                  <a href={`mailto:${tenantInfo.contact_email}`}>
                    <MenuItem onClick={handleHelpClose}>
                      <ListItemIcon>
                        <EmailIcon sx={{ fontSize: 'var(--icon-size-sm)' }} />
                      </ListItemIcon>
                      <ListItemText>{tenantInfo.contact_email}</ListItemText>
                    </MenuItem>
                  </a>
                )}
                {tenantInfo.contact_phone != null && (
                  <a href={`tel:${tenantInfo.contact_phone}`}>
                    <MenuItem onClick={handleHelpClose}>
                      <ListItemIcon>
                        <CallIcon sx={{ fontSize: 'var(--icon-size-sm)' }} />
                      </ListItemIcon>
                      <ListItemText>{tenantInfo.contact_phone}</ListItemText>
                    </MenuItem>
                  </a>
                )}
                {!separateTenant && [
                  <MenuItem key="help-support-availability" disabled>
                    <ListItemIcon>
                      <SupportAgentIcon sx={{ fontSize: 'var(--icon-size-sm)' }} />
                    </ListItemIcon>
                    <ListItemText>24/7 Technical Support &mdash; US Based Team</ListItemText>
                  </MenuItem>,
                  <Divider key="help-support-divider" />,
                ]}
                <MenuItem disabled>
                  <ListItemIcon>
                    <SettingsIcon sx={{ fontSize: 'var(--icon-size-sm)' }} />
                  </ListItemIcon>
                  <ListItemText>{renderFrontEndVersion()}</ListItemText>
                </MenuItem>
              </Menu>

              <Tooltip title={t('general.settings')}>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (location.pathname.startsWith('/user')) {
                      navigate(-1);
                    } else {
                      navigate('/user');
                    }
                  }}
                  sx={
                    location.pathname.startsWith('/user')
                      ? {
                          color: 'primary.main',
                          bgcolor: 'var(--sidebar-item-active)',
                          '&:hover': { bgcolor: 'var(--sidebar-item-active)' },
                        }
                      : undefined
                  }
                >
                  <SettingsRoundedIcon sx={{ fontSize: 'var(--icon-size-sm)' }} />
                </IconButton>
              </Tooltip>

              <Tooltip title={schemeTooltip}>
                <IconButton size="small" onClick={handleSchemeToggle} aria-label="color scheme">
                  <SchemeIcon sx={{ fontSize: 'var(--icon-size-sm)' }} />
                </IconButton>
              </Tooltip>

              {/* The MODERN_UI grant (user- or customer-level; staff implied)
                  exposes the switch. It never selects the build — whoever is
                  on this build stays regardless. */}
              {!__DISABLE_THEME_SWITCH__ && userHasFeature(user, MODERN_UI_FEATURE) && (
                <Tooltip title={themePref === THEME_MODERN ? 'Switch to Classic Theme' : 'Switch to Modern Theme'}>
                  <IconButton size="small" onClick={handleThemeToggle}>
                    <PaletteIcon sx={{ fontSize: 'var(--icon-size-sm)' }} />
                  </IconButton>
                </Tooltip>
              )}

              {isCollapsedMode ? (
                <Divider flexItem sx={{ my: 0.5, borderColor: 'var(--sidebar-divider)' }} />
              ) : (
                <Box sx={{ flex: 1 }} />
              )}

              <Tooltip title={t('general.signOut')}>
                <IconButton
                  size="small"
                  onClick={() => navigate('/logout')}
                  sx={{ '&:hover': { color: 'error.main', bgcolor: 'var(--status-bad-tint)' } }}
                >
                  <LogoutRoundedIcon sx={{ fontSize: 'var(--icon-size-sm)' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Stack>
      </Drawer>
    </>
  );
}

const LeftNavContainer = withRouter(LeftNav);

export default LeftNavContainer;
export { SIDEBAR_WIDTH_EXPANDED, SIDEBAR_WIDTH_COLLAPSED };
